import { Children, useMemo, type ReactNode } from 'react';
import styled from 'styled-components';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import { Toast } from '../../components/toast/toast';
import { economicActivities } from '../../mocks/EconomicActivities';
import { ivaStatuses } from '../../mocks/IvaStatuses';
import { clientData, ReferrerName } from '../../types/interfaces';
import { useReferrerNames } from '../../hooks/useReferrerNames';
import { useOfficers } from '../../hooks/useOfficers';
import { useClientTheme } from '../../context/ThemeContext';
import { useConfig } from '../../context/ConfigContext';
import { useManagements } from '../../hooks/useManagements';
import { getManagementDisplayLabel } from '../../utils/managementDisplay';
import { getOfficerDisplayLabel } from '../../utils/officerDisplay';
import { formatCurrency } from '../../utils/Masks';
import { useOperationTypes } from '../../hooks/useOperationTypes';
import theme from '../../styles/theme';
import { normalizeDgiStatus } from '../Origination/normalizeUruguayClientData';

const SyndicatedChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing['spacing-1']} ${theme.spacing['spacing-2']};
  border-radius: ${theme.borderRadius['border-radius-2']};
  background: ${theme.colors.info.bg.weak};
  color: ${theme.colors.info.text.regular};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  text-transform: none;
  white-space: nowrap;
`;

interface BasicDataProps {
  clientInfo: clientData;
  isSyndicated?: boolean;
}

const formatSummaryDate = (
  dateStr: string | null | undefined,
  separator: '/' | '-' = '-',
): string => {
  if (!dateStr?.trim()) return '—';
  const trimmed = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split('-');
    return `${day}${separator}${month}${separator}${year}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed) && separator === '-') {
    return trimmed.replace(/\//g, '-');
  }
  return trimmed;
};

const UY_SUMMARY_GRID_3: React.CSSProperties = {
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
};

const UY_SUMMARY_GRID_DATOS_GENERALES: React.CSSProperties = {
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr)) minmax(0, 0.55fr)',
};

const UY_SUMMARY_ROW_GAP = '16px';

interface SummaryFieldProps {
  label: string;
  value?: React.ReactNode;
}

const SummaryField: React.FC<SummaryFieldProps> = ({ label, value }) => (
  <Card display='flex' gap='4px' width='100%' style={{ minWidth: 0 }}>
    <P type='2' variant='neutral' weight='bold' withMargin={false}>
      {label}
    </P>
    <P type='2' variant='neutral' withMargin={false}>
      {value ?? '—'}
    </P>
  </Card>
);

const padToGridColumns = (cells: React.ReactNode[], columnCount: number) => {
  const padded = [...cells];
  while (padded.length < columnCount) {
    padded.push(<span key={`grid-empty-${padded.length}`} aria-hidden='true' />);
  }
  return padded;
};

interface SummaryFieldRowProps {
  children: React.ReactNode;
  variant?: 'threeCol' | 'datosGenerales';
}

const SummaryFieldRow: React.FC<SummaryFieldRowProps> = ({
  children,
  variant = 'threeCol',
}) => {
  const columnCount = variant === 'datosGenerales' ? 4 : 3;
  return (
    <Card
      display='grid'
      gap='24px'
      width='100%'
      style={
        variant === 'datosGenerales'
          ? UY_SUMMARY_GRID_DATOS_GENERALES
          : UY_SUMMARY_GRID_3
      }
    >
      {padToGridColumns(Children.toArray(children), columnCount)}
    </Card>
  );
};

const BasicData: React.FC<BasicDataProps> = ({ clientInfo, isSyndicated = false }) => {
  const { referrerNames } = useReferrerNames();
  const { officers } = useOfficers();
  const { clientTheme } = useClientTheme();
  const appConfig = useConfig();
  const isClientCategoryTypeEnabled = appConfig.isClientCategoryTypeEnabled ?? false;
  const isApplicationEffectiveDateEnabled = appConfig.isApplicationEffectiveDateEnabled ?? false;
  const isApplicationCreditTypeEnabled = appConfig.isApplicationCreditTypeEnabled ?? false;
  const isManagementSelectionEnabled = appConfig.isManagementSelectionEnabled ?? false;
  const isOriginationDataModalEnabled = appConfig.isOriginationDataModalEnabled ?? false;
  const isUruguayEnabled = appConfig.isUruguayEnabled ?? false;
  const { managements } = useManagements(isManagementSelectionEnabled);
  const { operationTypes } = useOperationTypes();

  const formatBooleanAsYesNo = (value: boolean | null | undefined) =>
    value === true ? 'Sí' : value === false ? 'No' : '—';
  const categoryTypeDisplayName = clientInfo.category_type?.name;

  const operationTypeLabel = useMemo(
    () => {
      if (clientInfo.operation_type_label) return clientInfo.operation_type_label;
      if (!clientInfo.operation_type_id) return '—';
      return operationTypes.find(ot => ot.id === clientInfo.operation_type_id)?.label || '—';
    },
    [clientInfo.operation_type_id, clientInfo.operation_type_label, operationTypes]
  );

  const currentSgrBalanceLabel = useMemo(() => {
    if (!isOriginationDataModalEnabled) return null;
    const balance = clientInfo.current_sgr_balance;
    if (balance === null || balance === undefined) return '—';
    return `$ ${formatCurrency(balance, true)}`;
  }, [clientInfo.current_sgr_balance, isOriginationDataModalEnabled]);

  const managementLabel = useMemo(
    () => getManagementDisplayLabel(clientInfo, managements),
    [clientInfo, clientInfo.management_id, clientInfo.management_label, managements]
  );

  const officerDisplayLabel = useMemo(
    () => getOfficerDisplayLabel(clientInfo.officer, officers),
    [clientInfo.officer, officers],
  );

  // Helper para obtener el label del referrer name
  const getReferrerNameLabel = (referrerName: string | ReferrerName | undefined): string => {
    if (!referrerName) return '—';
    if (typeof referrerName === 'string') {
      const found = referrerNames.find(rn => rn.code === referrerName);
      return found ? found.name : referrerName;
    }
    return referrerName.name;
  };

  const renderSectionHeader = (title: string) => (
    <Card
      variant={clientTheme ? 'primary' : 'success'}
      modifier='weak'
      spacing={'2'}
      display='flex'
    >
      <H
        type='3'
        variant='neutral-1'
        withMargin={false}
        weight='bold'
        modifier='strong'
      >
        {title}
      </H>
    </Card>
  );

  const renderUruguaySection = (
    title: string,
    content: ReactNode,
    options: { isFirst?: boolean; isLast?: boolean } = {},
  ) => (
    <Card
      margin={{
        top: options.isFirst ? '0' : '4',
        bottom: options.isLast ? '4' : '0',
      }}
      width='100%'
    >
      {renderSectionHeader(title)}
      <Card margin={{ top: '4' }} width='100%'>
        {content}
      </Card>
    </Card>
  );

  const renderUruguaySummary = () => (
    <>
      {renderUruguaySection(
        'Datos Generales',
        <>
          <P type='1' weight='bold' variant='neutral' withMargin={false}>
            {clientInfo.basic_data.company_name}
          </P>
          <Card
            display='flex'
            flexDirection='column'
            gap={UY_SUMMARY_ROW_GAP}
            margin={{ top: '4' }}
            width='100%'
          >
            <SummaryFieldRow variant='datosGenerales'>
              <SummaryField
                label='Personería:'
                value={clientInfo.basic_data.legal_entity}
              />
              <SummaryField
                label='Fecha de constitución:'
                value={formatSummaryDate(clientInfo.basic_data.date_incorporation, '/')}
              />
            </SummaryFieldRow>

            <SummaryFieldRow variant='datosGenerales'>
              {isClientCategoryTypeEnabled && (
                <SummaryField
                  label='Rubro:'
                  value={categoryTypeDisplayName}
                />
              )}
              <SummaryField
                label='Referido / Vendedor:'
                value={getReferrerNameLabel(clientInfo.referrer_name)}
              />
              {isManagementSelectionEnabled && (
                <SummaryField
                  label='Gerencia:'
                  value={managementLabel}
                />
              )}
            </SummaryFieldRow>

            <SummaryFieldRow variant='datosGenerales'>
              <SummaryField
                label='Domicilio fiscal:'
                value={clientInfo.basic_data.company_address}
              />
              <SummaryField
                label='Departamento:'
                value={clientInfo.basic_data.state}
              />
              <SummaryField
                label='Localidad:'
                value={clientInfo.basic_data.city}
              />
              <SummaryField
                label='Código Postal:'
                value={clientInfo.basic_data.postal_code}
              />
            </SummaryFieldRow>

            <SummaryFieldRow variant='datosGenerales'>
              <SummaryField
                label='Domicilio legal:'
                value={clientInfo.basic_data.legal_company_address}
              />
              <SummaryField
                label='Departamento:'
                value={clientInfo.basic_data.legal_state}
              />
              <SummaryField
                label='Localidad:'
                value={clientInfo.basic_data.legal_city}
              />
              <SummaryField
                label='Código Postal:'
                value={clientInfo.basic_data.legal_postal_code}
              />
            </SummaryFieldRow>

            <Card display='flex' gap='4px' margin={{ top: '2' }}>
              <P type='2' variant='neutral' weight='bold'>
                Comentarios:
              </P>
              <P type='2' variant='neutral'>
                {clientInfo.extra_comments}
              </P>
            </Card>

            <SummaryFieldRow variant='datosGenerales'>
              <SummaryField label='Motivo:' value={clientInfo.reason} />
              {isApplicationEffectiveDateEnabled && (
                <SummaryField
                  label='Fecha de vigencia:'
                  value={formatSummaryDate(clientInfo.effective_date, '/')}
                />
              )}
              {isApplicationCreditTypeEnabled && (
                <SummaryField
                  label='Clase de crédito:'
                  value={clientInfo.credit_type_name || '—'}
                />
              )}
            </SummaryFieldRow>

            <Card display='flex' gap='4px' margin={{ top: '2' }}>
              <P type='2' variant='neutral' weight='bold'>
                Reseñas:
              </P>
              <P type='2' variant='neutral'>
                {clientInfo.review}
              </P>
            </Card>
          </Card>
        </>,
        { isFirst: true },
      )}

      {renderUruguaySection(
        'Proveedor del Estado',
        <SummaryFieldRow>
          <SummaryField
            label='Vínculo contractual:'
            value={formatBooleanAsYesNo(clientInfo.basic_data.contractual_link)}
          />
          <SummaryField
            label='Persona políticamente expuesta:'
            value={formatBooleanAsYesNo(clientInfo.basic_data.politically_exposed)}
          />
          <SummaryField
            label='Registro Único de Proveedores del Estado (RUPE):'
            value={formatBooleanAsYesNo(clientInfo.basic_data.state_supplier_registry)}
          />
        </SummaryFieldRow>,
      )}

      {renderUruguaySection(
        'Inscripción DGI',
        <SummaryFieldRow>
          <SummaryField
            label='Fecha de emisión:'
            value={formatSummaryDate(clientInfo.basic_data.dgi_issue_date)}
          />
          <SummaryField
            label='Estado DGI:'
            value={
              normalizeDgiStatus(clientInfo.basic_data.dgi_status) ||
              clientInfo.basic_data.dgi_status ||
              '—'
            }
          />
          <SummaryField
            label='Fecha de vencimiento:'
            value={formatSummaryDate(clientInfo.basic_data.dgi_expiration_date)}
          />
        </SummaryFieldRow>,
      )}

      {renderUruguaySection(
        'Certificado común BPS',
        <SummaryFieldRow>
          <SummaryField
            label='Fecha de vencimiento:'
            value={formatSummaryDate(clientInfo.basic_data.bps_expiration_date)}
          />
          <SummaryField
            label='Fecha de emisión:'
            value={formatSummaryDate(clientInfo.basic_data.bps_issue_date)}
          />
          <SummaryField
            label='Número certificado BPS:'
            value={clientInfo.basic_data.bps_certificate_number}
          />
        </SummaryFieldRow>,
        { isLast: true },
      )}
    </>
  );

  return (
    <Card width='100%'>
      <Toast id='application-toast' />

      {isUruguayEnabled ? (
        renderUruguaySummary()
      ) : (
        <>
      <Card
        variant={clientTheme ? 'primary' : 'success'}
        modifier='weak'
        spacing={'2'}
        display='flex'
        justifyContent='space-between'
      >
        <H
          type='3'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Datos Generales
        </H>
      </Card>

      <Card display='flex' alignItems='center' gap='8px' margin={{ top: '2' }}>
        <P type='1' weight='bold' variant='neutral' withMargin={false}>
          {clientInfo.basic_data.company_name}
        </P>
        {isSyndicated && (
          <SyndicatedChip data-testid="syndicated-badge">
            Es sindicada
          </SyndicatedChip>
        )}
      </Card>

      <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Personería:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.legal_entity}
          </P>
        </Card>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Fecha de constitución:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.date_incorporation || '—'}
          </P>
        </Card>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Condición frente al IVA:
          </P>
          <P type='2' variant='neutral'>
            {
              ivaStatuses.find(
                (item) => item.code === clientInfo.basic_data.iva_status
              )?.label
            }
          </P>
        </Card>
      </Card>

      <Card display='flex' gap='4px' width='35%' margin={{ top: '2' }}>
        <P type='2' variant='neutral' weight='bold'>
          Actividad económica:
        </P>
        <P type='2' variant='neutral'>
          {(() => {
            const activityValue = clientInfo.basic_data.activity;
            if (!activityValue) return '—';
            
            let foundActivity = null;
            
            if (typeof activityValue === 'string') {
              const trimmedValue = activityValue.trim();
              if (trimmedValue === '') return '—';
              
              const activityCode = parseInt(trimmedValue, 10);
              if (!isNaN(activityCode)) {
                foundActivity = economicActivities.find(
                  (item) => item.code === activityCode
                );
              } else {
                foundActivity = economicActivities.find(
                  (item) => item.label === trimmedValue
                );
                if (!foundActivity) {
                  return trimmedValue;
                }
              }
            } else if (typeof activityValue === 'number') {
              foundActivity = economicActivities.find(
                (item) => item.code === activityValue
              );
            }
            
            return foundActivity?.label || String(activityValue) || '—';
          })()}
        </P>
      </Card>

      <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Domicilio fiscal:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.company_address}
          </P>
        </Card>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Localidad:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.city}
          </P>
        </Card>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Provincia:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.state}
          </P>
        </Card>
      </Card>

      <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Teléfono:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.phone_number || (clientInfo as any).contact_phone}
          </P>
        </Card>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Correo electrónico:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.email || (clientInfo as any).contact_email}
          </P>
        </Card>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Referido/Vendedor:
          </P>
          <P type='2' variant='neutral'>
            {getReferrerNameLabel(clientInfo.referrer_name)}
          </P>
        </Card>
      </Card>

      <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
        <Card display='flex' gap='4px' margin={{ top: '2' }} width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Motivo:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.reason}
          </P>
        </Card>

        {isApplicationEffectiveDateEnabled && (
          <Card display='flex' gap='4px' margin={{ top: '2' }} width='30%'>
            <P type='2' variant='neutral' weight='bold'>
              Fecha de vigencia:
            </P>
            <P type='2' variant='neutral'>
              {clientInfo.effective_date ? new Date(clientInfo.effective_date).toLocaleDateString('es-AR') : '—'}
            </P>
          </Card>
        )}

        {isApplicationCreditTypeEnabled && (
          <Card display='flex' gap='4px' margin={{ top: '2' }} width='30%'>
            <P type='2' variant='neutral' weight='bold'>
              Clase de crédito:
            </P>
            <P type='2' variant='neutral'>
              {clientInfo.credit_type_name || '—'}
            </P>
          </Card>
        )}
      </Card>

      {isOriginationDataModalEnabled && (
        <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>
              Tipo de operación:
            </P>
            <P type='2' variant='neutral'>
              {operationTypeLabel}
            </P>
          </Card>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>
              Saldo vigente con la SGR:
            </P>
            <P type='2' variant='neutral'>
              {currentSgrBalanceLabel}
            </P>
          </Card>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>
              Analista de Riesgo:
            </P>
            <P type='2' variant='neutral'>
              {clientInfo.assigned_risk_analyst_label || '—'}
            </P>
          </Card>
        </Card>
      )}

      {!isManagementSelectionEnabled && (
        <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>
              Oficial:
            </P>
            <P type='2' variant='neutral'>
              {officerDisplayLabel}
            </P>
          </Card>
          {isClientCategoryTypeEnabled && (
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>
                Rubro:
              </P>
              <P type='2' variant='neutral'>
                {categoryTypeDisplayName || '—'}
              </P>
            </Card>
          )}
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>
              Comentarios:
            </P>
            <P type='2' variant='neutral'>
              {clientInfo.extra_comments}
            </P>
          </Card>
          {!isClientCategoryTypeEnabled && <Card display='flex' gap='4px' width='30%'></Card>}
        </Card>
      )}

      {isManagementSelectionEnabled && !isClientCategoryTypeEnabled && (
        <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>
              Gerencia:
            </P>
            <P type='2' variant='neutral'>
              {managementLabel}
            </P>
          </Card>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>
              Comentarios:
            </P>
            <P type='2' variant='neutral'>
              {clientInfo.extra_comments}
            </P>
          </Card>
          <Card display='flex' gap='4px' width='30%'></Card>
        </Card>
      )}

      {isManagementSelectionEnabled && isClientCategoryTypeEnabled && (
        <>
          <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>
                Gerencia:
              </P>
              <P type='2' variant='neutral'>
                {managementLabel}
              </P>
            </Card>
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>
                Rubro:
              </P>
              <P type='2' variant='neutral'>
                {categoryTypeDisplayName || '—'}
              </P>
            </Card>
            <Card display='flex' gap='4px' width='30%'></Card>
          </Card>
          <Card display='flex' gap='4px' margin={{ top: '2' }} width='100%'>
            <P type='2' variant='neutral' weight='bold'>
              Comentarios:
            </P>
            <P type='2' variant='neutral'>
              {clientInfo.extra_comments}
            </P>
          </Card>
        </>
      )}

      <Card display='flex' gap='4px' margin={{ top: '2' }}>
        <P type='2' variant='neutral' weight='bold'>
          Reseñas:
        </P>
        <P type='2' variant='neutral'>
          {clientInfo.review}
        </P>
      </Card>
        </>
      )}
    </Card>
  );
};

// Mantener compatibilidad con el uso anterior como función
const renderBasicData = (
  clientInfo: clientData,
  options?: { isSyndicated?: boolean }
): JSX.Element => {
  return (
    <BasicData
      clientInfo={clientInfo}
      isSyndicated={options?.isSyndicated ?? !!clientInfo.is_syndicated}
    />
  );
};

export default renderBasicData;
export { BasicData };
